import { useEffect, useRef } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useOnboarding, MarkableStep } from '../context/OnboardingContext';

interface AutoDetectOptions {
  householdId: string | null | undefined;
  userId: string | null | undefined;
}

/**
 * Watches Firestore collections and auto-marks onboarding steps complete
 * when the corresponding actions are detected in the app.
 *
 * Mount once on the Dashboard. Dormant when onboarding is already complete.
 */
export function useOnboardingAutoDetect({ householdId, userId }: AutoDetectOptions): void {
  const { steps, isComplete, markStep } = useOnboarding();
  const markedRef = useRef<Set<MarkableStep>>(new Set());

  // Helper — mark a step exactly once per session (avoids redundant Firestore writes)
  const mark = (step: MarkableStep) => {
    if (markedRef.current.has(step)) return;
    if (steps[step]) return; // already marked in Firestore
    markedRef.current.add(step);
    markStep(step).catch(console.error);
  };

  // ── Creator: invite member ────────────────────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'creator' || steps.inviteMember || !householdId) return;

    const unsub = onSnapshot(doc(db, 'households', householdId), (snap) => {
      if (!snap.exists()) return;
      const memberIds: string[] = snap.data().memberIds ?? [];
      if (memberIds.length > 1) mark('inviteMember');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.inviteMember, householdId]);

  // ── Creator: add 3 tasks ──────────────────────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'creator' || steps.addThreeTasks || !householdId) return;

    const tasksRef = collection(db, 'households', householdId, 'tasks');
    const unsub = onSnapshot(tasksRef, (snap) => {
      const userCreatedTasks = snap.docs.filter(d => !d.data().fromTemplate);
      if (userCreatedTasks.length >= 3) mark('addThreeTasks');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.addThreeTasks, householdId]);

  // ── Creator: add calendar event ───────────────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'creator' || steps.addCalendarEvent || !householdId) return;

    const eventsRef = collection(db, 'households', householdId, 'events');
    const unsub = onSnapshot(eventsRef, (snap) => {
      if (snap.size >= 1) mark('addCalendarEvent');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.addCalendarEvent, householdId]);

  // ── Creator: set up grocery categories ───────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'creator' || steps.setupGroceryCategories || !householdId) return;

    const catRef = collection(db, 'households', householdId, 'settings');
    const unsub = onSnapshot(catRef, (snap) => {
      const catDoc = snap.docs.find((d) => d.id === 'customGroceryCategories');
      if (!catDoc) return;
      const categories = catDoc.data().categories ?? {};
      if (Object.keys(categories).length >= 1) mark('setupGroceryCategories');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.setupGroceryCategories, householdId]);

  // ── Creator: add maintenance item ─────────────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'creator' || steps.addMaintenanceItem || !householdId) return;

    const maintRef = collection(db, 'households', householdId, 'maintenance');
    const unsub = onSnapshot(maintRef, (snap) => {
      if (snap.size >= 1) mark('addMaintenanceItem');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.addMaintenanceItem, householdId]);

  // ── Joiner: create first task ─────────────────────────────────────────────

  useEffect(() => {
    if (isComplete || steps.userType !== 'joiner' || steps.createFirstTask || !householdId || !userId) return;

    const tasksRef = collection(db, 'households', householdId, 'tasks');
    const myTasksQuery = query(tasksRef, where('createdBy', '==', userId));
    const unsub = onSnapshot(myTasksQuery, (snap) => {
      if (snap.size >= 1) mark('createFirstTask');
    });
    return unsub;
  }, [isComplete, steps.userType, steps.createFirstTask, householdId, userId]);
}
